import { type OrganizationsClaims, organizationsClaimsApi } from 'api-validations/user-api'
import { useEffect } from 'react'
import { useAuth } from 'shared-ui/auth-provider'
import { isNonNil } from 'utils'

import { usePanoramaQuery } from '../api-provider'
import { useJoinOrgAsSupporter } from '../support/useJoinOrgAsSupporter'
import { useOrgCodeOrThrow } from './ActiveOrgCodeProvider'
import { DEMO_ORG_CODE } from './useAccount'
import { type OrgInfo, useOrgInfoLookup } from './useOrgInfo'

export enum OrgAccessStatus {
  Pending = 'pending',
  Granted = 'granted',
  Denied = 'denied'
}

export type OrgAccess =
  | { status: OrgAccessStatus.Pending }
  | { status: OrgAccessStatus.Granted; orgInfo: OrgInfo }
  | { status: OrgAccessStatus.Denied }

type OrgEntryDecision = { join: false; access: OrgAccessStatus } | { join: true }

/**
 * Decides what arriving at the active org should do: either report an access
 * status as-is, or attempt a supporter join first, reporting Pending until the
 * attempt settles and denying access if it fails.
 *
 * Access follows the backend, which authorizes every org-scoped request against
 * a role in the org and honors no platform-wide bypass. A user with no role in
 * the org is denied the whole org rather than shown a page whose every request
 * then fails.
 */
const decideOrgEntry = ({
  orgCode,
  claims
}: {
  orgCode: string
  claims: OrganizationsClaims | undefined
}): OrgEntryDecision => {
  // The demo operation is not a real org and has no claims; every account may
  // enter it.
  if (orgCode === DEMO_ORG_CODE) return { join: false, access: OrgAccessStatus.Granted }

  if (claims === undefined) return { join: false, access: OrgAccessStatus.Pending }

  if (claims.directRole !== null || claims.viaDealer) {
    return { join: false, access: OrgAccessStatus.Granted }
  }

  if (claims.support) {
    // No endpoint tells a non-member whether an org is supportable, so the join
    // is attempted optimistically. A successful join gives the support user a
    // role and access; a rejection means the org never opted in to support or
    // does not exist, so there is no access to it.
    return { join: true }
  }

  return { join: false, access: OrgAccessStatus.Denied }
}

/**
 * Fires the supporter join at most once per org. The mutation instance
 * outlives org switches (only the :orgCode param changes), so its state only
 * speaks for the org it was fired for.
 */
const useSupporterJoinAttempt = (params: { username: string; orgCode: string } | undefined) => {
  const joinOrg = useJoinOrgAsSupporter()

  const attemptedThisOrg = params !== undefined && joinOrg.variables?.orgCode === params.orgCode

  const pendingJoin =
    params !== undefined && !attemptedThisOrg && !joinOrg.isPending ? params : undefined

  const { mutate } = joinOrg
  useEffect(() => {
    if (pendingJoin !== undefined) mutate(pendingJoin)
  }, [pendingJoin, mutate])

  return { failed: attemptedThisOrg && joinOrg.isError }
}

/**
 * Whether the user may enter the active organization.
 *
 * A support user arriving at a supportable org they have no role in is joined
 * as a supporter automatically; if that join is refused they are denied, as is
 * any other user with no role in the org.
 */
export const useOrgAccess = (): OrgAccess => {
  const orgCode = useOrgCodeOrThrow()
  const username = useAuth().user?.username ?? ''

  const claims = usePanoramaQuery(
    organizationsClaimsApi,
    { username, orgCode },
    { enabled: username !== '', select: (data) => data.items[0] }
  )

  const decision = decideOrgEntry({ orgCode, claims: claims.data })
  const join = useSupporterJoinAttempt(decision.join ? { username, orgCode } : undefined)
  const lookup = useOrgInfoLookup(orgCode)

  // The server authorizes every request on its own; when the claims lookup
  // itself fails, fail open to the page rather than telling a member they
  // lack access.
  if (!claims.isError) {
    if (decision.join) {
      return { status: join.failed ? OrgAccessStatus.Denied : OrgAccessStatus.Pending }
    }
    if (decision.access !== OrgAccessStatus.Granted) return { status: decision.access }
  }

  if (isNonNil(lookup.orgInfo)) return { status: OrgAccessStatus.Granted, orgInfo: lookup.orgInfo }
  return { status: lookup.isPending ? OrgAccessStatus.Pending : OrgAccessStatus.Denied }
}
